import React from 'react';
import { Button, Spinner, Tooltip } from '@heroui/react';
import { RefreshCw, Users, BookOpen, GraduationCap, TrendingUp, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { AnalyticsSummary } from '@/services/apis/analytics';
import SummaryCard from './SummaryCard';
import DailyActiveUsersChart from './DailyActiveUsersChart';
import CompletionFunnelChart from './CompletionFunnelChart';
import TopContentTable from './TopContentTable';
import DistributionChart from './DistributionChart';
import StatCard from './StatCard';

interface DashboardTabProps {
  data: AnalyticsSummary | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ data, isLoading, isRefreshing, onRefresh }) => {
  const { t } = useTranslation('admin');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" label={t('dashboard.loading')} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-default-500">
        <p>{t('dashboard.noData')}</p>
        <Button color="primary" variant="flat" className="mt-4" onPress={onRefresh}>
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  }

  const topCourses = data.courses.topEnrolled.map((c) => ({
    id: c.courseId,
    name: c.name,
    count: c.enrollments,
  }));

  const topGuides = data.guides.topViewed.map((g) => ({
    id: g.guideId,
    name: g.title,
    count: g.viewCount,
  }));

  const roleDistribution = [
    { label: t('dashboard.roles.learner'), value: data.users.byRole.learner, color: 'bg-blue-500' },
    { label: t('dashboard.roles.creator'), value: data.users.byRole.creator, color: 'bg-purple-500' },
    { label: t('dashboard.roles.admin'), value: data.users.byRole.admin, color: 'bg-amber-500' },
  ];

  const difficultyDistribution = [
    { label: t('dashboard.difficulty.beginner'), value: data.guides.byDifficulty.beginner, color: 'bg-emerald-500' },
    {
      label: t('dashboard.difficulty.intermediate'),
      value: data.guides.byDifficulty.intermediate,
      color: 'bg-amber-500',
    },
    { label: t('dashboard.difficulty.advanced'), value: data.guides.byDifficulty.advanced, color: 'bg-red-500' },
  ];

  const formatTime = (minutes: number | null) => {
    if (minutes === null) return '-';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const cachedTime = data.cachedAt ? new Date(data.cachedAt).toLocaleTimeString() : '';

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('dashboard.title')}</h1>
          <p className="text-sm text-default-500">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {cachedTime && (
            <span className="text-xs text-default-400">
              {t('dashboard.lastUpdated')}: {cachedTime}
            </span>
          )}
          <Tooltip content={t('dashboard.refreshTooltip')}>
            <Button
              isIconOnly
              variant="flat"
              onPress={onRefresh}
              isLoading={isRefreshing}
              aria-label={t('dashboard.refresh')}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </Tooltip>
        </div>
      </motion.div>

      {/* Key Metrics Summary */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.sections.overview')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title={t('dashboard.metrics.totalUsers')}
            value={data.users.total}
            subtitle={`${data.users.engagementRate}% ${t('dashboard.metrics.engaged')}`}
            icon={Users}
            colorClass="blue"
            delay={0}
          />
          <SummaryCard
            title={t('dashboard.metrics.totalCourses')}
            value={data.courses.total}
            subtitle={`${data.courses.published} ${t('dashboard.metrics.published')}`}
            icon={GraduationCap}
            colorClass="purple"
            delay={0.1}
          />
          <SummaryCard
            title={t('dashboard.metrics.totalGuides')}
            value={data.guides.total}
            subtitle={`${data.guides.published} ${t('dashboard.metrics.published')}`}
            icon={BookOpen}
            colorClass="emerald"
            delay={0.2}
          />
          <SummaryCard
            title={t('dashboard.metrics.activeLearners')}
            value={data.users.activeLearners}
            subtitle={t('dashboard.metrics.withProgress')}
            icon={TrendingUp}
            colorClass="amber"
            delay={0.3}
          />
        </div>
      </section>

      {/* Activity & Engagement */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.sections.activity')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DailyActiveUsersChart data={data.users.dailyActiveUsers} />
          <CompletionFunnelChart data={data.crossCutting.completionFunnel} />
        </div>
      </section>

      {/* Course Metrics */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.sections.courses')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label={t('dashboard.stats.enrollments')} value={data.courses.totalEnrollments} delay={0} />
          <StatCard label={t('dashboard.stats.uniqueEnrolled')} value={data.courses.uniqueUsersEnrolled} delay={0.1} />
          <StatCard
            label={t('dashboard.stats.completionRate')}
            value={data.courses.completionRate}
            suffix="%"
            delay={0.2}
          />
          <StatCard
            label={t('dashboard.stats.avgTime')}
            value={formatTime(data.courses.avgTimeToComplete)}
            delay={0.3}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopContentTable
            title={t('dashboard.topCourses')}
            data={topCourses}
            countLabel={t('dashboard.table.enrollments')}
            emptyMessage={t('dashboard.noCoursesYet')}
            delay={0.4}
          />
          <DistributionChart title={t('dashboard.usersByRole')} data={roleDistribution} delay={0.5} />
        </div>
      </section>

      {/* Guide Metrics */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.sections.guides')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label={t('dashboard.stats.guideCompletionRate')}
            value={data.guides.completionRate}
            suffix="%"
            delay={0}
          />
          <StatCard
            label={t('dashboard.stats.avgQuizScore')}
            value={data.guides.avgQuizScore !== null ? data.guides.avgQuizScore : '-'}
            suffix={data.guides.avgQuizScore !== null ? '%' : ''}
            delay={0.1}
          />
          <StatCard label={t('dashboard.stats.chatSessions')} value={data.guides.chatSessionsTotal} delay={0.2} />
          <StatCard label={t('dashboard.stats.inProgress')} value={data.courses.inProgressUsers} delay={0.3} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TopContentTable
            title={t('dashboard.topGuides')}
            data={topGuides}
            countLabel={t('dashboard.table.views')}
            emptyMessage={t('dashboard.noGuidesYet')}
            delay={0.4}
          />
          <DistributionChart title={t('dashboard.guidesByDifficulty')} data={difficultyDistribution} delay={0.5} />
        </div>
      </section>

      {/* Content Utilization */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">{t('dashboard.sections.utilization')}</h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 rounded-xl p-6 border border-indigo-200 dark:border-indigo-800"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Main Metric */}
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-full bg-indigo-100 dark:bg-indigo-900/50">
                <Target className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                  {t('dashboard.contentUtilization')}
                </p>
                <p className="text-4xl font-bold text-indigo-900 dark:text-indigo-100">
                  {data.crossCutting.contentUtilization.overallRate}%
                </p>
                <p className="text-xs text-indigo-600 dark:text-indigo-400">
                  {data.crossCutting.contentUtilization.uniqueUsersWithContent} /{' '}
                  {data.crossCutting.contentUtilization.totalUsers} {t('dashboard.utilizationDescription')}
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="flex flex-col sm:flex-row gap-4 lg:gap-8">
              <div className="text-center px-4 py-2 rounded-lg bg-white/50 dark:bg-black/20">
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                  {data.crossCutting.contentUtilization.courseInteractionRate}%
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {t('dashboard.utilization.courseInteraction')}
                </p>
              </div>
              <div className="text-center px-4 py-2 rounded-lg bg-white/50 dark:bg-black/20">
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                  {data.crossCutting.contentUtilization.guideInteractionRate}%
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  {t('dashboard.utilization.guideInteraction')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default DashboardTab;
